import React, { useEffect, useState } from 'react';
import { FaTrash, FaTimes, FaExclamationCircle } from 'react-icons/fa';

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, productName }) => {
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      setError(error.message || 'Failed to delete product. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose} style={{
        background: 'rgba(0, 0, 0, 0)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000
      }} />
      <div className="modal-content" style={{
        animation: 'slideIn 0.4s ease-out',
        background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
        borderRadius: '20px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        padding: '2.5rem',
        maxWidth: '450px',
        width: '90%',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1001
      }}>
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }

            @keyframes slideIn {
              from { transform: translate(-50%, -60%); opacity: 0; }
              to { transform: translate(-50%, -50%); opacity: 1; }
            }

            @keyframes shake {
              0%, 100% { transform: translateX(0); }
              10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
              20%, 40%, 60%, 80% { transform: translateX(5px); }
            }

            .error-modal {
              animation: shake 0.5s ease-in-out;
            }

            .error-icon-container {
              background: linear-gradient(145deg, #ff4444, #cc0000);
              box-shadow: 0 4px 15px rgba(255, 68, 68, 0.3);
              width: 60px;
              height: 60px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              margin: 0 auto 1rem;
            }

            .error-message {
              color: #ff4444;
              text-align: center;
              font-weight: 500;
              margin: 1rem 0;
              padding: 1rem;
              background: rgba(255, 68, 68, 0.1);
              border-radius: 8px;
              border: 1px solid rgba(255, 68, 68, 0.2);
            }

            .delete-icon-container {
              background: linear-gradient(145deg, #ff4444, #cc0000);
              box-shadow: 0 4px 15px rgba(255, 68, 68, 0.3);
              width: 60px;
              height: 60px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              margin: 0 auto 1rem;
            }

            .delete-btn {
              background: linear-gradient(145deg, #ff4444, #cc0000);
              box-shadow: 0 4px 15px rgba(255, 68, 68, 0.2);
              transition: all 0.3s;
              padding: 0.8rem 1.5rem;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 500;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 0.5rem;
              cursor: pointer;
            }

            .delete-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(255, 68, 68, 0.3);
              background: linear-gradient(145deg, #cc0000, #990000);
            }

            .delete-btn:active {
              transform: translateY(0);
            }

            .cancel-btn {
              background: linear-gradient(145deg, #6c757d, #5a6268);
              box-shadow: 0 4px 15px rgba(108, 117, 125, 0.2);
              transition: all 0.3s;
              padding: 0.8rem 1.5rem;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 500;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .cancel-btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(108, 117, 125, 0.3);
              background: linear-gradient(145deg, #5a6268, #4e555b);
            }

            .cancel-btn:active {
              transform: translateY(0);
            }

            .modal-header {
              text-align: center;
              margin-bottom: 1.5rem;
            }

            .modal-header h3 {
              margin: 0;
              color: #2c3e50;
              font-size: 1.5rem;
              font-weight: 600;
            }

            .modal-body {
              margin-bottom: 2rem;
            }

            .modal-body p {
              color: #666;
              text-align: center;
              line-height: 1.6;
            }

            .modal-body span {
              color: #2c3e50;
              font-weight: 500;
            }

            .modal-footer {
              display: flex;
              justify-content: flex-end;
              gap: 1rem;
            }
          `}
        </style>

        <div className="modal-header">
          <h3>Delete Product</h3>
        </div>

        <div className="modal-body">
          {error ? (
            <>
              <div className="error-icon-container">
                <FaExclamationCircle className="text-white text-2xl" />
              </div>
              <div className="error-message">
                {error}
              </div>
            </>
          ) : (
            <>
              <div className="delete-icon-container">
                <FaTrash className="text-white text-2xl" />
              </div>
              <p>
                Are you sure you want to delete <span>"{productName}"</span>?
                This action cannot be undone.
              </p>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            onClick={onClose}
            className="cancel-btn text-white"
          >
            Cancel
          </button>
          {!error && (
            <button
              onClick={handleConfirm}
              className="delete-btn text-white"
            >
              <FaTrash className="text-sm" />
              <span>Delete</span>
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default DeleteConfirmationModal; 